import { Badge, Button, IconButton, Stack, Tooltip, Typography } from '@chronosphereio/chrono-ui';
import { InputBase, InputBaseProps } from '@mui/material';
import { Close as CloseIcon, KeyboardReturn as KeyboardReturnIcon } from 'mdi-material-ui';
import { useQueryAssistant } from './query-assistant-context';
import { useSuggestQueryFn } from './query-assistant-hooks';
import { getCommandSymbol, ShortcutPill } from './ShortcutPill';
import { ThinkingState } from './ThinkingState';

export function QueryAssistant() {
  const suggestQuery = useSuggestQueryFn();

  const {
    closeAssistant,
    query,
    onChange,
    inputValue,
    setInputValue,
    hasInputValue,
    isLoading,
    setIsLoading,
    previousPrompts,
    setPreviousPrompts,
    history,
    setHistory,
    errorMessage,
    setErrorMessage,
    setSuggestion,
    handleCancel,
    cancelAndSetAbortController,
  } = useQueryAssistant();

  // Handle submit (i.e. generate query)
  const handleSubmit = async () => {
    if (!suggestQuery || !hasInputValue || isLoading) return;

    setIsLoading(true);
    setErrorMessage(null);

    const abortController = new AbortController();
    cancelAndSetAbortController(abortController);

    const input = inputValue.trim();
    const existingQuery = query;

    try {
      const suggestion = await suggestQuery({ input, existingQuery, history, abortSignal: abortController.signal });

      setSuggestion({ ...suggestion, metadata: { ...suggestion.metadata, input, existingQuery } });
      onChange(suggestion.query);
      setHistory((history) => [
        ...history,
        { type: 'request' as const, content: input, existingQuery },
        { type: 'response' as const, content: suggestion.query, status: 'success' as const },
      ]);
    } catch (error) {
      if (!abortController.signal.aborted) {
        let errMessage: string | undefined;
        if (error instanceof Error && error.name === 'RequestNotSupportedError') {
          errMessage = error.message;
        } else if (error instanceof Error && error.name === 'TimeoutError') {
          errMessage = 'Query generation timed out. Please try again.';
        } else {
          errMessage = 'Failed to generate a query. Please try again.';
        }

        setErrorMessage(errMessage);
        setHistory((history) => [
          ...history,
          { type: 'request' as const, content: input, existingQuery },
          { type: 'response' as const, content: errMessage, status: 'error' as const },
        ]);
      }
    } finally {
      if (!abortController.signal.aborted) {
        setIsLoading(false);
        setPreviousPrompts((prompts) => [...prompts, input]);
        setInputValue('');
      }
    }
  };

  return (
    <Stack gap={0.5}>
      <Stack
        gap={0.5}
        paddingX={1.5}
        paddingY={1}
        maxHeight={200}
        overflow="auto"
        borderRadius={1}
        bgcolor="background.paper"
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Stack flexGrow={1}>
            {previousPrompts.length === 0 ? (
              <ActiveInput onSubmit={handleSubmit} />
            ) : (
              <PreviousInput value={previousPrompts[0]} />
            )}
          </Stack>
          <Tooltip Content="Close">
            <IconButton aria-label="Close" size="small" onClick={closeAssistant}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        {previousPrompts.length > 0 && (
          <>
            {previousPrompts.slice(1).map((value, index) => (
              <PreviousInput key={index} value={value} />
            ))}
            <ActiveInput onSubmit={handleSubmit} />
          </>
        )}
        {isLoading && (
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <ThinkingState
              messages={['Searching metrics...', 'Analyzing labels...', 'Generating PromQL...']}
              maxDuration={4}
            />
            <Button
              size="small"
              variant="tertiary"
              onClick={handleCancel}
              sx={{ px: 1, py: 0.25, whiteSpace: 'nowrap' }}
            >
              Cancel
            </Button>
          </Stack>
        )}
      </Stack>
      {errorMessage && (
        <Typography variant="body2" color="error">
          {errorMessage}
        </Typography>
      )}
    </Stack>
  );
}

function ActiveInput({ onSubmit }: { onSubmit: () => Promise<void> }) {
  const {
    query,
    inputValue,
    setInputValue,
    hasInputValue,
    isLoading,
    suggestion,
    handleAcceptAndRun,
    handleAccept,
    handleReject,
    closeAssistant,
  } = useQueryAssistant();

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Input
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        inputProps={{ readOnly: isLoading }}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={async (e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            // Cmd+Enter: Accept + run query
            if (suggestion) {
              e.preventDefault();
              e.stopPropagation();
              handleAcceptAndRun();
            }
          } else if (e.key === 'Enter') {
            // Enter: Generate query
            e.preventDefault();
            e.stopPropagation();
            await onSubmit();
          } else if (e.key === 'Tab') {
            // Tab: Accept query
            if (suggestion) {
              e.preventDefault();
              e.stopPropagation();
              handleAccept();
            }
          } else if (e.key === 'Escape') {
            // Escape: Reject query or close
            if (suggestion) {
              e.preventDefault();
              e.stopPropagation();
              handleReject();
            } else {
              e.preventDefault();
              e.stopPropagation();
              closeAssistant();
            }
          } else if (e.metaKey && !e.shiftKey && e.key === 'k') {
            // Cmd+K: Close assistant
            e.preventDefault();
            e.stopPropagation();
            closeAssistant();
          }
        }}
        placeholder={query ? 'Refine your query' : 'Describe your query'}
      />
      {!isLoading && hasInputValue && (
        <Button size="small" variant="tertiary" onClick={onSubmit} sx={{ px: 1, py: 0.25, whiteSpace: 'nowrap' }}>
          Generate
          <ShortcutPill sx={{ ml: 0.5 }}>
            <KeyboardReturnIcon />
          </ShortcutPill>
        </Button>
      )}
    </Stack>
  );
}

function PreviousInput({ value }: { value?: string }) {
  return <Input value={value} disabled sx={{ color: 'text.secondary' }} />;
}

function Input(props: InputBaseProps) {
  return (
    <InputBase
      {...props}
      fullWidth
      sx={{
        backgroundColor: 'inherit',
        '&.MuiInputBase-root': { py: '2px !important' },
        ...props.sx,
      }}
    />
  );
}

export function QueryAssistantButton() {
  const { openAssistant } = useQueryAssistant();

  return (
    <Tooltip
      placement="left"
      Content={<Badge variant="new" size="small" label="Early access" sx={{ whiteSpace: 'nowrap' }} />}
      componentsProps={{
        tooltip: {
          sx: { backgroundColor: 'transparent', boxShadow: 'none', p: 0 },
        },
        popper: {
          // Bring the tooltip closer to the anchor.
          modifiers: [{ name: 'offset', options: { offset: [0, -6] } }],
        },
      }}
    >
      <Button
        size="small"
        variant="secondary"
        onClick={openAssistant}
        sx={(t) => {
          const legacyBackground = t.palette.mode === 'light' ? t.palette.grey[50] : t.palette.background.paper;
          const legacyHoverBackground = t.palette.mode === 'light' ? t.palette.grey[100] : t.palette.action.hover;
          return {
            flexShrink: 0,
            px: 1,
            whiteSpace: 'nowrap',
            color: 'text.secondary',
            bgcolor: t.featureFlags.panwRebrandStubYear ? undefined : legacyBackground,
            border: t.featureFlags.panwRebrandStubYear ? undefined : `1px solid ${t.palette.divider}`,
            borderRadius: 1,
            '&:hover': {
              color: 'text.primary',
              bgcolor: t.featureFlags.panwRebrandStubYear ? undefined : legacyHoverBackground,
              border: t.featureFlags.panwRebrandStubYear ? undefined : `1px solid ${t.palette.divider}`,
            },
          };
        }}
      >
        Edit with AI
      </Button>
    </Tooltip>
  );
}

type QueryAssistantAcceptAndRejectProps = {
  onAccept?: () => void;
  onReject?: () => void;
};

export function QueryAssistantAcceptAndReject({ onAccept, onReject }: QueryAssistantAcceptAndRejectProps) {
  const { handleAcceptAndRun, handleAccept, handleReject } = useQueryAssistant();

  return (
    <Stack direction="row" gap={1}>
      <Button
        size="small"
        variant="primary"
        sx={{ pl: 1, pr: 0.5 }}
        onClick={() => {
          handleAcceptAndRun();
          onAccept?.();
        }}
      >
        Accept and run
        <ShortcutPill sx={{ ml: 1 }}>
          <Typography variant="code" sx={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 0.25 }}>
            {getCommandSymbol()} <KeyboardReturnIcon />
          </Typography>
        </ShortcutPill>
      </Button>
      <Button
        size="small"
        variant="secondary"
        sx={{ pl: 1, pr: 0.5 }}
        onClick={() => {
          handleAccept();
          onAccept?.();
        }}
      >
        Accept
        <ShortcutPill sx={{ ml: 1 }}>
          <Typography variant="code" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            Tab
          </Typography>
        </ShortcutPill>
      </Button>
      <Button
        size="small"
        variant="secondary"
        sx={{ pl: 1, pr: 0.5 }}
        onClick={() => {
          handleReject();
          onReject?.();
        }}
      >
        Reject
        <ShortcutPill sx={{ ml: 1 }}>
          <Typography variant="code" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            Esc
          </Typography>
        </ShortcutPill>
      </Button>
    </Stack>
  );
}
